import type {
    PaginatedFetch,
    PaginatedFetchOptions,
} from "~/services/ApiBaseService";
import { ApiBaseService } from "~/services/ApiBaseService";
import type { FieldDefinition } from "~/models/Automation";
import type { ApiResponse } from "~/types/ApiResponse";
import type { Person } from "~/models/Person";
import type { Brand } from "~/services/BrandService";
import type { Form } from "~/models/Form";
import type { FormField } from "~/models/FormField";
import type { StoreTemplate, Template } from "~/models/Template";
import { faClone, faPencil, faTrash } from "@fortawesome/pro-solid-svg-icons";
import type { JSONContent } from "@tiptap/vue-3";
import type { Locale } from "~/models/Localization";
import type { FetchOptions } from "ofetch";
import type { LengthAwarePaginator } from "~/types/LengthAwarePaginator";

export interface MailTemplateMetadata {
    tags?: string[];
    fields: FieldDefinition[];
    utm_campaign: {
        campaign: string | null;
        source: string | null;
        medium: string | null;
        content: string | null;
        term: string | null;
    };
}

export type MailBuilderType = "html" | "mjml" | "plain" | "block";

export interface MailTemplateTranslation {
    id: number;
    locale: Locale;
    subject: string | null;
    name: string | null;
    description: string | null;
    translations: Record<string, string | object | null>;
    created_at: string;
    updated_at: string;
}

export interface MailTemplate {
    id: string;
    brand: { id: string | null } | Brand;
    form: { id: string | null } | Form;
    form_field: { id: string | null } | FormField;
    locale: { id: number | null } | Locale;
    is_template: boolean;
    is_active_template: boolean;
    auto_translate: boolean;
    type: MailBuilderType;
    name: string;
    description: string;
    subject: string;
    translations?: MailTemplateTranslation[];
    text_data: string | null;
    json_data: JSONContent | null;
    metadata: MailTemplateMetadata;
    preview_url: string;
    created_at: string;
    updated_at: string;
}

export default class MailTemplateService extends ApiBaseService {
    public templates(
        options?: PaginatedFetchOptions<MailTemplate[]>,
    ): PaginatedFetch<MailTemplate[]> {
        return this.paginate<MailTemplate[]>(
            this.route("api.mail-templates.default", "/mail-templates/default"),
            options,
        );
    }

    public templatesRoute(): string {
        return this.route("api.mail-templates.index", "/mail-templates/default");
    }

    public templatesRequest(options?: FetchOptions): Promise<ApiResponse<MailTemplate[]>> {
        return this.request<ApiResponse<MailTemplate[]>>(this.templatesRoute(), options);
    }

    public list(
        options?: Partial<PaginatedFetchOptions>,
    ): PaginatedFetch<Person[]> {
        return this.paginate<Person[]>(
            this.route("api.mail-templates.index", "/mail-templates"),
            options as PaginatedFetchOptions,
        );
    }

    async store(
        data: Partial<
            Omit<MailTemplate, "id" | "brand" | "created_at" | "updated_at"> & {
                brand_id?: number;
            }
        >,
    ) {
        return this.request<ApiResponse<MailTemplate>>(
            this.route("api.mail-templates.store", "/mail-templates"),
            {
                method: "POST",
                body: data,
            },
        );
    }

    async show(id: string, query: Record<string, any> = {}) {
        return this.request<ApiResponse<MailTemplate>>(
            this.route(
                "api.mail-templates.show",
                "/mail-templates/{template}",
                {
                    template: id,
                },
            ),
            {
                method: "GET",
                query: toRef(query).value,
            },
        );
    }

    async preview(id: string, query: Record<string, any> = {}) {
        return this.request<ApiResponse<MailTemplate>>(this.preview_url(id), {
            method: "GET",
            query: toRef(query).value,
        });
    }

    async generateTranslation(id: string, locale_id: number) {
        return this.request<
            ApiResponse<Record<string, string | object | null>>
        >(
            this.route(
                "api.mail-templates.generate-translation",
                "/mail-templates/{id}/generate-translation/{locale_id}",
                {
                    id,
                    locale_id,
                },
            ),
            {
                method: "POST",
                body: {
                    locale_id,
                },
            },
        );
    }

    async updateTranslation(
        id: string,
        data: Partial<MailTemplateTranslation>,
    ) {
        return this.request<ApiResponse<MailTemplateTranslation>>(
            this.route(
                "api.mail-templates.update-translation",
                "/mail-templates/{id}/update-translation",
                {
                    id,
                },
            ),
            {
                method: "PATCH",
                body: data,
            },
        );
    }

    async deleteTranslation(
        id: string,
        translation_id: number,
    ) {
        return this.request<void>(
            this.route(
                "api.mail-templates.delete-translation",
                "/mail-templates/{id}/delete-translation/{translation_id}",
                {
                    id,
                    translation_id,
                },
            ),
            {
                method: "DELETE",
            },
        );
    }

    preview_url(id: string) {
        return (
            useRuntimeConfig().public.api.baseUrl +
            this.route(
                "api.public.emails.preview",
                "/public/emails/{template}/preview",
                {
                    template: id,
                },
            )
        );
    }

    preview_url_locale(id: string, locale: string) {
        return (
            useRuntimeConfig().public.api.baseUrl +
            this.route(
                "api.public.emails.preview",
                "/public/emails/{template}/preview/{locale}",
                {
                    template: id,
                    locale,
                },
            )
        );
    }

    public delete(id: string): Promise<null> {
        return this.request(
            this.route(
                "api.mail-templates.destroy",
                "/mail-templates/{template}",
                {
                    template: id,
                },
            ),
            {
                method: "DELETE",
            },
        );
    }

    async update(
        id: string,
        data: Partial<
            Omit<MailTemplate, "id" | "brand" | "created_at" | "updated_at"> & {
                brand_id?: number;
            }
        >,
    ): Promise<ApiResponse<MailTemplate>> {
        if (data.form_field?.id && !data.form?.id) {
            data.form_field = { id: null };
        }

        return this.request<ApiResponse<MailTemplate>>(
            this.route(
                "api.mail-templates.update",
                "/mail-templates/{template}",
                {
                    template: id,
                },
            ),
            {
                method: "PATCH",
                body: data,
            },
        );
    }

    public showTemplate(id: string): Promise<ApiResponse<Template>> {
        return this.request(
            this.route(
                "api.mail-templates.templates.store",
                "/mail-templates/{id}/template",
                {
                    id,
                },
            ),
        );
    }

    public storeTemplate(
        id: string,
        data: Partial<StoreTemplate>,
    ): Promise<ApiResponse<Template>> {
        if (!(data.banner instanceof File)) {
            delete data.banner;
        }

        return this.request(
            this.route(
                "api.mail-templates.templates.store",
                "/mail-templates/{id}/template",
                {
                    id,
                },
            ),
            {
                method: "POST",
                body: jsonToFormData(data, {
                    booleanAsNumbers: true,
                }),
            },
        );
    }

    public disableTemplate(uuid: string): Promise<ApiResponse<Template>> {
        return this.request(
            this.route(
                "api.mail-templates.templates.delete",
                "/mail-templates/{uuid}/template",
                {
                    uuid,
                },
            ),
            {
                method: "DELETE",
            },
        );
    }

    public enableTemplate(uuid: string): Promise<ApiResponse<Template>> {
        return this.request(
            this.route(
                "api.mail-templates.templates.restore",
                "/mail-templates/{uuid}/template/restore",
                {
                    uuid,
                },
            ),
            {
                method: "POST",
            },
        );
    }

    public duplicateEmail(email: MailTemplate) {
        return this.duplicate(email.id);
    }

    public duplicate(id: string): Promise<ApiResponse<MailTemplate>> {
        return this.request(
            this.route(
                "api.mail-templates.duplicate",
                "/mail-templates/{id}/duplicate",
                { id },
            ),
            {
                method: "POST",
                body: [],
            },
        );
    }

    public dropdown(
        email: MailTemplate,
        options: Partial<{
            deletable?: Ref<MailTemplate | undefined>;
            forDuplication?: Ref<MailTemplate | undefined>;
        }> = {},
    ) {
        return [
            [
                {
                    label: "Edit",
                    icon: faPencil,
                    to: `/emails/${email.id}`,
                },
                {
                    label: "Duplicate",
                    icon: faClone,
                    disabled: false,
                    click: () =>
                        options.forDuplication &&
                        (options.forDuplication.value = email),
                },
            ],
            [
                {
                    label: "Delete",
                    class: "text-red-500",
                    iconClass: "text-red-500",
                    icon: faTrash,
                    click: () => {
                        if (options.deletable) {
                            options.deletable.value = email;
                        } else {
                            useToast().add({
                                title: "Are you sure?",
                                description: `You're about to delete "${email.name ?? ""}."`,
                                actions: [
                                    {
                                        label: "Yes, delete",
                                        color: "primary",
                                        click: async () => {
                                            this.delete(email.id).then(
                                                async () => {
                                                    if (
                                                        useRoute().params.id ===
                                                        email.id
                                                    ) {
                                                        navigateTo({
                                                            name: "emails",
                                                        });
                                                    } else {
                                                        await refreshNuxtData();
                                                    }
                                                },
                                            );

                                            useToast().add({
                                                title: `${email.name} has been deleted.`,
                                            });
                                        },
                                    },
                                    {
                                        label: "No, cancel",
                                    },
                                ],
                            });
                        }
                    },
                },
            ],
        ];
    }

    public generateTemplateFieldsSuggestions(id: number|string, data: {
        title: string | undefined;
        description: string | undefined;
        tags: (string | undefined)[];
    }): Promise<ApiResponse<Template & { error?: string }>> {
        return this.request(
            this.route("api.mail-templates.template.generate-fields-suggestions", "/mail-templates/{id}/template/generate-fields-suggestions", {
                id
            }),
            {
                method: "POST",
                body: data,
            }
        );
    }
}
